import React from 'react';
import styled from 'styled-components';

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #718096;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
`;

const StatChange = styled.div`
  font-size: 14px;
  color: ${props => props.positive ? '#38a169' : '#e53e3e'};
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 20px 0;
`;

const PlaceholderChart = styled.div`
  height: 300px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 500;
`;

const RecentActivity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #667eea;
  }
`;

const ActivityText = styled.div`
  flex: 1;
  
  div:first-child {
    font-weight: 500;
    color: #1a202c;
    font-size: 14px;
  }
  
  div:last-child {
    color: #718096;
    font-size: 12px;
  }
`;

const Overview = () => {
  const stats = [
    {
      title: 'Total Balance',
      value: '$12,450.32',
      change: '+5.2%',
      positive: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: '24h P&L',
      value: '+$234.56',
      change: '+1.8%',
      positive: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
          <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
    },
    {
      title: 'Active Trades',
      value: '8',
      change: '+2',
      positive: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polygon points="10,8 16,12 10,16 10,8"></polygon>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'
    },
    {
      title: 'Win Rate',
      value: '73.2%',
      change: '+2.1%',
      positive: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)'
    }
  ];

  const activities = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
        </svg>
      ),
      title: 'Buy order executed',
      time: '2 minutes ago'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
        </svg>
      ),
      title: 'Sell order completed',
      time: '15 minutes ago'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h4l3 3 3-3h4a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-4"></path>
        </svg>
      ),
      title: 'Stop loss triggered',
      time: '1 hour ago'
    }
  ];

  return (
    <OverviewContainer>
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatTitle>{stat.title}</StatTitle>
              <StatIcon bgColor={stat.bgColor}>
                {stat.icon}
              </StatIcon>
            </StatHeader>
            <StatValue>{stat.value}</StatValue>
            <StatChange positive={stat.positive}>
              {stat.positive ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
                  <polyline points="17 18 23 18 23 12"></polyline>
                </svg>
              )}
              {stat.change}
            </StatChange>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartSection>
        <ChartCard>
          <ChartTitle>Portfolio Performance</ChartTitle>
          <PlaceholderChart>
            Portfolio Chart - Connect to your trading API
          </PlaceholderChart>
        </ChartCard>
        
        <ChartCard>
          <ChartTitle>Recent Activity</ChartTitle>
          <RecentActivity>
            {activities.map((activity, index) => (
              <ActivityItem key={index}>
                {activity.icon}
                <ActivityText>
                  <div>{activity.title}</div>
                  <div>{activity.time}</div>
                </ActivityText>
              </ActivityItem>
            ))}
          </RecentActivity>
        </ChartCard>
      </ChartSection>
    </OverviewContainer>
  );
};

export default Overview;
